import React, { Suspense, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import UserActionBar from '../UserActionBar/UserActionBar';
import { useAuth } from '../../hooks/useAuth';
import AdminShortcutsSheet from './ui/AdminShortcutsSheet';
import useAdminShortcuts from './ui/useAdminShortcuts';
import { AdminLayoutProvider } from './AdminLayoutContext';

const AdminSpotlight = React.lazy(() => import('./ui/AdminSpotlight'));

function AdminLayout() {
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const { user } = useAuth();
  const isPartnerAdmin = user?.role === 'partner_admin';
  // Floating bottom-right Dev Portal tile is the partner_admin canonical
  // affordance (Kyle + Shay's evpimpofficial=partner_admin owner-verifier
  // account). The role='admin' owner account gets a SEPARATE topbar toggle
  // in TopNav.jsx instead so it doesn't overlap with the admin shell UI.
  const showDevPortal = isPartnerAdmin;

  useAdminShortcuts({
    onOpenSpotlight: () => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('admin:open-spotlight'));
      }
    },
    onOpenShortcuts: () => setShortcutsOpen(true),
    onCloseShortcuts: () => setShortcutsOpen(false),
  });

  useEffect(() => {
    if (!isPartnerAdmin || typeof document === 'undefined') {
      return undefined;
    }
    const destructivePattern = /\b(restart|clear cache|clear all|re-run|rerun|force|delete|remove|reset|sync|ingest|approve|reject|validate|grade|grant|create account|launch test)\b/i;
    const apply = () => {
      document.querySelectorAll('.admin-layout--partner-readonly button').forEach((button) => {
        const text = [
          button.textContent,
          button.getAttribute('title'),
          button.getAttribute('aria-label'),
        ].filter(Boolean).join(' ');
        if (destructivePattern.test(text) && !button.closest('.admin-workspace__rail')) {
          button.setAttribute('hidden', 'true');
          button.setAttribute('aria-hidden', 'true');
        }
      });
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isPartnerAdmin]);

  return (
    <AdminLayoutProvider>
      <div className={`admin-layout${isPartnerAdmin ? ' admin-layout--partner-readonly' : ''}`}>
        <UserActionBar variant="admin" />
        <Outlet />
        {showDevPortal ? (
          <a
            href="http://[fd7a:115c:a1e0::1701:473f]:3050/"
            target="_blank"
            rel="noopener noreferrer"
            className="admin-layout__partner-dev-portal"
            title="Open the partner-trusted Debono dev environment (port 3050, tailnet-only)"
          >
            <svg
              className="admin-layout__partner-dev-portal-icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="2,12 6,12 8,8 11,16 13,4 15,20 17,12 22,12" />
            </svg>
            Dev Portal
          </a>
        ) : null}
        <AdminShortcutsSheet open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
        <Suspense fallback={null}>
          <AdminSpotlight />
        </Suspense>
      </div>
    </AdminLayoutProvider>
  );
}

export default AdminLayout;
